import React from "react";
import { Clock, Timer, AlertCircle, PoundSterling } from "lucide-react";

function gbp(p) {
  if (p == null) return "—";
  return `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtHM(mins) {
  const m = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return h > 0 ? `${h}h ${rest}m` : `${rest}m`;
}

/**
 * Live job timer + overtime panel.
 * Shows actual elapsed vs booked time. When running, ticks every 15s.
 * `audience` = "customer" | "driver" — only the wording differs.
 *
 * Expects `timer` shape:
 *   { started_at, ended_at, is_running, actual_minutes, booked_minutes,
 *     overtime_minutes, overtime_blocks_30, overtime_pence_cash, ot_pence_per_30 }
 */
export default function JobTimerPanel({ timer, audience = "customer" }) {
  const [, force] = React.useState(0);

  // Live tick when the job is still running
  React.useEffect(() => {
    if (!timer?.is_running) return;
    const id = setInterval(() => force((x) => x + 1), 15000);
    return () => clearInterval(id);
  }, [timer?.is_running]);

  if (!timer) return null;

  // Re-compute live actual minutes when running (server figure goes stale)
  let actualMin = timer.actual_minutes;
  if (timer.is_running && timer.started_at) {
    const startMs = new Date(timer.started_at).getTime();
    actualMin = (Date.now() - startMs) / 60000;
  }
  const bookedMin = timer.booked_minutes || 0;
  const overMin = Math.max(0, actualMin - bookedMin);
  // Recompute overtime cash live (round UP to 30min blocks)
  const blocks = Math.ceil(overMin / 30);
  const liveOtPence = blocks * (timer.ot_pence_per_30 || 0);

  const overtime = overMin > 0;
  const dotColor = timer.is_running ? "#5BB247" : overtime ? "#A66800" : "#1E4D55";

  return (
    <div
      data-testid="job-timer-panel"
      className={`mt-6 p-5 border ${
        overtime
          ? "bg-[#F5A623]/5 border-[#F5A623]/40"
          : timer.is_running
          ? "bg-[#5BB247]/5 border-[#5BB247]/40"
          : "bg-[#F5F5F5] border-[#0A0A0A]/10"
      }`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span
            data-testid="job-timer-status-dot"
            className={`inline-block w-2 h-2 rounded-full ${timer.is_running ? "animate-pulse" : ""}`}
            style={{ backgroundColor: dotColor }}
          />
          <p className="font-display text-base font-bold tracking-tight text-[#0A0A0A]">
            {timer.is_running ? "Job in progress" : overtime ? "Finished with overtime" : "Finished within booked time"}
          </p>
        </div>
        {timer.is_running && (
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#475569]">LIVE · ticks every 15s</span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="job-timer-grid">
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569]">Actual time</p>
          <p className="mt-1 font-display text-2xl font-black tracking-tighter text-[#0A0A0A]" data-testid="job-timer-actual">
            {fmtHM(actualMin)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569]">Booked time</p>
          <p className="mt-1 font-display text-2xl font-black tracking-tighter text-[#0A0A0A]">
            {fmtHM(bookedMin)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569]">Overtime</p>
          <p
            className={`mt-1 font-display text-2xl font-black tracking-tighter ${overtime ? "text-[#A66800]" : "text-[#0A0A0A]"}`}
            data-testid="job-timer-overtime"
          >
            {overtime ? fmtHM(overMin) : "—"}
          </p>
        </div>
      </div>

      {overtime && (
        <div
          data-testid="job-timer-overtime-cash"
          className="mt-4 pt-4 border-t border-[#F5A623]/30 flex items-start gap-3"
        >
          <PoundSterling size={18} className="text-[#A66800] shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-[#0A0A0A]">
              {audience === "driver"
                ? `${gbp(liveOtPence)} owed to you in CASH on the day`
                : `${gbp(liveOtPence)} owed to your driver in CASH on the day`}
            </p>
            <p className="mt-1 text-xs text-[#475569]">
              Overtime billed in 30-min blocks at {gbp(timer.ot_pence_per_30 || 0)}/30min. Raanah doesn&apos;t take a cut on overtime — it&apos;s paid directly between you and the driver.
            </p>
          </div>
        </div>
      )}

      {!overtime && timer.is_running && bookedMin > 0 && actualMin / bookedMin > 0.85 && (
        <div className="mt-4 pt-4 border-t border-[#5BB247]/30 flex items-start gap-2 text-xs text-[#475569]">
          <AlertCircle size={14} className="text-[#A66800] shrink-0 mt-0.5" />
          <span>
            Booked time runs out in <strong>{fmtHM(bookedMin - actualMin)}</strong>. After that, overtime kicks in at <strong>{gbp(timer.ot_pence_per_30 || 0)}/30min</strong> (cash on the day).
          </span>
        </div>
      )}
    </div>
  );
}
